/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `IlluEigentuemerProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface IlluEigentuemerProps {
    /**
     * A string representing the CSS class to be applied to the IlluEigentuemerIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#000'
     */
    color1?: string;
    /**
     * A number or string representing the height of the IlluEigentuemerIcon element.
     *
     * @default 96
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'IlluEigentuemer'
     */
    testId?: string;
    /**
     * A number or string representing the height of the IlluEigentuemerIcon element.
     *
     * @default 96
     */
    width?: number | string;
}

/**
 * The `IlluEigentuemer` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `IlluEigentuemerProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the IlluEigentuemerIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the IlluEigentuemerIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the IlluEigentuemerIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const IlluEigentuemerComponent = <IlluEigentuemer className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const IlluEigentuemer = ({
    className = '',
    color1 = '#000',
    height = 96,
    ref,
    testId = 'IlluEigentuemer',
    width = 96
}: IlluEigentuemerProps) => (
    <svg
        ref={ref}
        aria-hidden="true"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 96 96"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M33.38 67.22c3.18.16 6.19 1.53 8.44 3.84a13.4 13.4 0 0 1 3.78 9.3v6.04a1.2 1.2 0 0 1-2.4 0v-6.04l-.02-.53a11 11 0 0 0-3.08-7.09c-1.84-1.89-4.28-3-6.85-3.13l-.51-.01H15.27a10.3 10.3 0 0 0-7.4 3.16 11 11 0 0 0-3.07 7.66v5.98a1.2 1.2 0 0 1-2.4 0v-5.98c0-3.5 1.35-6.85 3.76-9.34a12.7 12.7 0 0 1 9.1-3.88h17.48zM56.18 6a9.2 9.2 0 0 1 5.65 1.94l.32.26 28.18 24.25.38.33a9.3 9.3 0 0 1 2.89 6.75v36.38a9.3 9.3 0 0 1-9.25 9.29H54.34a1.2 1.2 0 0 1 0-2.4H67.2V49.6a2.8 2.8 0 0 0-2.8-2.8h-16a2.8 2.8 0 0 0-2.8 2.8v12.03a1.2 1.2 0 0 1-2.4 0V49.61a5.2 5.2 0 0 1 5.2-5.21h16a5.2 5.2 0 0 1 5.2 5.2v33.2h14.75a6.83 6.83 0 0 0 6.85-6.89V39.54a7 7 0 0 0-2.43-5.27L60.59 10.02a6.8 6.8 0 0 0-8.83 0l-25.78 22.1a1.2 1.2 0 1 1-1.56-1.83L50.2 8.2A9.2 9.2 0 0 1 56.18 6M24.6 37.2a12.6 12.6 0 1 1 0 25.2 12.6 12.6 0 0 1 0-25.2m0 2.4a10.2 10.2 0 1 0 0 20.4 10.2 10.2 0 0 0 0-20.4" fill={color1} />
    </svg>
);

IlluEigentuemer.displayName = 'IlluEigentuemer';